import axios, { AxiosResponse } from "axios";
import { apiUrls } from "../../config/apiUrls";
import {
  IApiServiceResponse,
  Note,
  SendTripDocumentRequest,
  SendTripDocumentResponse,
  TransportPDFRequest,
  TripAcceptance,
  TripCountResponse,
  TripDetails,
  TripPODApiResponse,
  TripPODRequest,
  TripRequestBody,
  TripResponse,
  TripStop,
  UpdateLicencePlateRequest,
  UpdateLicencePlateResponse,
  UploadPODRequestBuffer,
  UploadPODResponseBuffer,
} from "../../types/api";

export const tripsService = {



  fetchTrips: async (requestData: TripRequestBody): Promise<TripResponse[]> => {
    try {

      const response: AxiosResponse<TripResponse[]> = await axios.post(
        apiUrls.getTrips,
        requestData,
        {
          headers: {
            'Content-Type': 'application/json; charset=utf-8',
            'Authorization': "Bearer " + window.sessionStorage.getItem('accessToken')
          }
        }
      );

      return response.data; // Restituisci i dati dalla risposta
    } catch (error) {
      console.error("Error fetching trips:", error);
      throw error; // Propaga l'errore
    }
  },

  getTripHeaderById: async (id: number): Promise<TripDetails> => {
    try {


      const url = apiUrls.getTripHeaderById.replace("{id}", id.toString());
      const response: AxiosResponse<TripDetails> = await axios.get(url, {
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          'Authorization': "Bearer " + window.sessionStorage.getItem('accessToken')
        }
      });

      return response.data; // Restituisci i dati dalla risposta
    } catch (error) {
      console.error(`Error fetching trip header for ID ${id}:`, error);
      throw error; // Propaga l'errore
    }
  },

  getLicensePlatesByTripId: async (id: number): Promise<{ licensePlate: string; equipmentType: string, isUpdatable: boolean }[]> => {
    try {
      const url = apiUrls.getTripLicensePlates.replace("{id}", id.toString());
      const response = await axios.get<{ licensePlate: string; equipmentType: string, isUpdatable: boolean }[]>(url, {
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          'Authorization': "Bearer " + window.sessionStorage.getItem('accessToken')
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching license plates for trip ID ${id}:`, error);
      throw error;
    }
  },

  updateLicensePlate: async (
    requestData: UpdateLicencePlateRequest
  ): Promise<UpdateLicencePlateResponse> => {
    try {
      const response: AxiosResponse<UpdateLicencePlateResponse> = await axios.put(apiUrls.putTripLicensePlate, requestData, {
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          'Authorization': "Bearer " + window.sessionStorage.getItem('accessToken')
        }
      });
      return response.data;
    } catch (error) {
      console.error("Error updating licence plate:", error);
      throw error;
    }
  },
  updateTripAcceptance: async (
    tripId: number,
    status: string,
    user: string
  ) => {
    const payload = {
      tripID: tripId,
      user: user,
      status: status,
    };


    const response = await axios.put(apiUrls.putTripAcceptanceById, payload, {
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        'Authorization': "Bearer " + window.sessionStorage.getItem('accessToken')
      }
    });
    return response.data;
  },

  getTripAcceptanceById: async (
    TripId: number,
    legacyCodes: { legacycode: string; company: string }[]
  ): Promise<TripAcceptance> => {
    try {
      const response: AxiosResponse<TripAcceptance> = await axios.post(
        apiUrls.getTripAcceptanceById,
        { TripId, legacyCodes },
        {
          headers: {
            'Content-Type': 'application/json; charset=utf-8',
            'Authorization': "Bearer " + window.sessionStorage.getItem('accessToken')
          }
        } // Aggiungi l'header Authorization
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching trip acceptance:", error);
      throw error;
    }
  },

  getTripTimelineById: async (id: number): Promise<TripStop[]> => {
    const url = apiUrls.getTripTimelineById.replace("{id}", id.toString());
    const response = await axios.get<TripStop[]>(url, {
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        'Authorization': "Bearer " + window.sessionStorage.getItem('accessToken')
      }
    });
    return response.data;
  },

  getTripNotesById: async (id: number): Promise<Note[]> => {
    try {
      const url = apiUrls.getTripNotesById.replace("{id}", id.toString());
      const response: AxiosResponse<Note[]> = await axios.get(url, {
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          'Authorization': "Bearer " + window.sessionStorage.getItem('accessToken')
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching trip notes for ID ${id}:`, error);
      throw error;
    }
  },

  getDocumentsNote: async (documentId: number): Promise<any> => {
    try {
      const url = apiUrls.getDocumentsNote.replace("{id}", documentId.toString());
      const response: AxiosResponse<any> = await axios.get(url, {
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          'Authorization': "Bearer " + window.sessionStorage.getItem('accessToken')
        },
        params: { caller: "vlmportal" }, 
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching document notes for ID ${documentId}:`, error);
      throw error;
    }
  },


  getTripsCount: async (requestData: TripRequestBody): Promise<TripCountResponse> => {
    try {
      const response: AxiosResponse<TripCountResponse> = await axios.post(
        apiUrls.getTripsCount,
        requestData,
        {
          headers: {
            'Content-Type': 'application/json; charset=utf-8',
            'Authorization': "Bearer " + window.sessionStorage.getItem('accessToken')
          }
        } // Aggiungi l'header Authorization
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching trips count:", error);
      throw error;
    }
  },

  getLastOrdersPositions: async (data: {
    orderList: {
      erpSystem: string;
      erpCompany: string;
      orderType: number;
      orderNumber: string;
    }[];
  }): Promise<IApiServiceResponse> => {
    try {
      const response: AxiosResponse<IApiServiceResponse> = await axios.post(
        apiUrls.getLastOrdersPositions,
        data,
        {
          headers: {
            'Content-Type': 'application/json; charset=utf-8',
            'Authorization': "Bearer " + window.sessionStorage.getItem('accessToken'),
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching last order positions:", error);
      throw error;
    }
  },

  getAllOrdersPositions: async (data: {
    erpSystem: string;
    erpCompany: string;
    orderType: number;
    orderNumber: string;
  }): Promise<IApiServiceResponse> => {
    try {
      const response: AxiosResponse<IApiServiceResponse> = await axios.post(
        apiUrls.getAllOrdersPositions,
        data,
        {
          headers: {
            'Content-Type': 'application/json; charset=utf-8',
            'Authorization': "Bearer " + window.sessionStorage.getItem('accessToken'),
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching last order positions:", error);
      throw error;
    }
  },

  getPODDocNumbers: async (
    requestData: TripPODRequest
  ): Promise<TripPODApiResponse> => {
    try {
      //   const url = apiUrls.getTripPOD.replace("{id}", requestData.Cod_Viaggio.toString());
      const response: AxiosResponse<TripPODApiResponse> = await axios.post(
        apiUrls.getPODDocNumbers,
        requestData,
        {
          headers: {
            'Content-Type': 'application/json; charset=utf-8',
            'Authorization': "Bearer " + window.sessionStorage.getItem('accessToken'),
          },
          params: { "caller": 'vlmportal' }
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching POD DocNumbers:", error);
      throw error;
    }
  },


  uploadPOD: async (requestData: UploadPODRequestBuffer): Promise<UploadPODResponseBuffer> => {
    try {

      const response: AxiosResponse<UploadPODResponseBuffer> = await axios.post(
        apiUrls.uploadPOD,
        requestData.file, // Invia direttamente il FormData
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "Authorization": "Bearer " + window.sessionStorage.getItem("accessToken"),
          },
          params: { "caller": 'vlmportal' }
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error uploading POD document:", error);
      throw error;
    }
  },

  sendTripDocument: async (payload: SendTripDocumentRequest): Promise<SendTripDocumentResponse> => {
    console.log('[DEBUG] sendTripDocument called with payload:', payload);
    console.log('[DEBUG] sendTripDocument URL:', apiUrls.sendTripDocument);
    
    try {
      const response: AxiosResponse<SendTripDocumentResponse> = await axios.post(
        apiUrls.sendTripDocument,
        payload,
        {
          headers: {
            "Content-Type": "application/json; charset=utf-8",
            "Authorization": "Bearer " + window.sessionStorage.getItem("accessToken"),
          },
          params: { "caller": 'vlmportal' }
        }

      );

      console.log('[DEBUG] sendTripDocument response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error("Errore nella chiamata sendTripDocument:", error);
      console.error("Errore dettagliato:", error.response?.data || error.message);
      throw error.response?.data || error.message || "Errore sconosciuto";
    }
  },

  getDocumentDetails: async (id: string): Promise<any> => {
    try {
      const url = apiUrls.getDocumentDetails.replace('{id}', id);
      const response: AxiosResponse<any> = await axios.get(url, {
        responseType: 'arraybuffer',
        headers: {
          "Content-Type": "application/json; charset=utf-8",
          "Authorization": "Bearer " + window.sessionStorage.getItem("accessToken"),
        },
      });
      return response.data;
    } catch (error: any) {
      console.error(`Error fetching document details for ID ${id}:`, error);
      throw error.response?.data || error.message || "Errore sconosciuto";
    }
  },

  getDocumentDetails2: async (id: string): Promise<any> => {
    try {
      const url = apiUrls.getDocumentDetails.replace('{id}', id);
      const response: AxiosResponse<any> = await axios.get(url, {
        responseType: 'arraybuffer',
        headers: {
          "Content-Type": "application/json; charset=utf-8",
          "Authorization": "Bearer " + window.sessionStorage.getItem("accessToken"),
        },
        params: { "caller": 'vlmportal' }
      });
      return response.data;
    } catch (error: any) {
      console.error(`Error fetching document details for ID ${id}:`, error);
      throw error.response?.data || error.message || "Errore sconosciuto";
    }
  },

  getTermsAndConditions: async (
    accountId: string,
    contactId: string,
    lang: string,
    readonly: boolean = false
  ): Promise<{
    duvri: { name: string; description: string; documentBody: string; accepted: boolean };
    generalTerms: { name: string; description: string; documentBody: string; accepted: boolean };
    sop?: { name: string; description: string; documentBody: string; accepted: boolean };
  }> => {
    try {
      const response = await axios.get(apiUrls.getTermsAndConditions, {
        headers: {
          "Content-Type": "application/json; charset=utf-8",
          "Authorization": "Bearer " + window.sessionStorage.getItem("accessToken"),
        },
        params: { accountId, contactId, lang, readonly }
      });
      return response.data.responseBody;
    } catch (error: any) {
      console.error("Error fetching Terms and Conditions:", error);
      throw error.response?.data || error.message || "Errore sconosciuto";
    }
  },

  generateTransportPDF: async (
    requestData: TransportPDFRequest
  ): Promise<any> => {
    try {

      const response: AxiosResponse<any> = await axios.post(
        apiUrls.transportPDF,
        requestData,
        {
          headers: {
            "Content-Type": "application/json",
            "Authorization": "Bearer " + window.sessionStorage.getItem("accessToken"),
          },
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
        }
      );

      return response;
    } catch (error) {
      console.error("Error generating transport PDF:", error);

      throw error;
    }
  }


};
